import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { db, userCreationAuth } from "../../../firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  getDocs,
  collection,
} from "firebase/firestore";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import * as XLSX from "xlsx";
import "./UserManagement.css";
import "../User Admin/AddUserModal/AddUserModal.css";
import EditUserModal from "./editModal";
import { FaSearch, FaPlus, FaEdit, FaSyncAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import { getModulesForUserSubsidiaries } from "../../../utils/moduleUtils";
import {
  extractDomainFromEmail,
  filterUsersByPermissions,
  getAllUsersFromEnterprises,
  getDomainsBySubsidiaries,
  getUserByDomains,
  getUserBySubsidiaries,
  sortUsersAlphabetically,
  updateUserField,
} from "../../../utils/userHelpers";

function UserManagement({ userRole, accounts, userInformations }) {
  const [usersData, setUsersData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUser, setEditingUser] = useState(null); // Para controlar o popup de edição
  const [editedName, setEditedName] = useState("");
  const [loading, setLoading] = useState(false);
  const [permittedDomains, setPermittedDomains] = useState([]);
  const [subsidiariesUsers, setSubsidiariesUsers] = useState([]);
  const [availableSubsidiaries, setAvailableSubsidiaries] = useState([]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const combinedUsers = useMemo(() => {
    const usersMap = new Map();

    if (userRole === "admin_global") {
      usersData.forEach((user) => {
        const key = `${user.email}-${user.domain}`;
        usersMap.set(key, user);
      });
    } else {
      usersData.forEach((user) => {
        const key = `${user.email}-${user.domain}`;
        usersMap.set(key, user);
      });

      subsidiariesUsers.forEach((subsidiaryUser) => {
        const key = `${subsidiaryUser.email}-${subsidiaryUser.domain}`;
        if (!usersMap.has(key)) {
          usersMap.set(key, subsidiaryUser);
        }
      });
    }

    // Retornar os usuários únicos
    return Array.from(usersMap.values());
  }, [usersData, subsidiariesUsers, userRole]);

  useEffect(() => {
    const loadUsers = async () => {
      const users = await getUserBySubsidiaries(userInformations.subsidiaries);
      setSubsidiariesUsers(users);
    };

    loadUsers();
  }, [userInformations.subsidiaries]);

  const filteredUsers = filterUsersByPermissions(combinedUsers, {
    userRole,
    permittedDomains,
    subsidiariesUsers,
    searchTerm,
    userSubsidiaries: userInformations.subsidiaries,
  });

  const loadUsersData = useCallback(async () => {
    setLoading(true); // Inicia o carregamento
    let usersList = [];

    // Se for admin global, carregar todos os usuários de todos os domínios
    if (userRole === "admin_global") {
      usersList = await getAllUsersFromEnterprises({
        includeDomain: true, // IMPORTANTE : Componente utiliza o domain
        sorted: false,
      });
    } else {
      // Se for admin local, carregar usuários apenas dos domínios permitidos
      const domains = await getDomainsBySubsidiaries(
        userInformations.subsidiaries
      );
      setPermittedDomains(domains);

      usersList = await getUserByDomains(domains);
    }

    // Ordena e define os dados de usuários no estado
    setUsersData(sortUsersAlphabetically(usersList));
    setLoading(false); // Finaliza o carregamento
  }, [userRole, userInformations.domains, sortUsersAlphabetically]);

  // Load the users' data whenever `loadUsersData` or its dependencies change
  useEffect(() => {
    loadUsersData();
  }, [loadUsersData]);

  // Função para habilitar/desabilitar um usuário
  const toggleUserStatus = async (email, domain, isActive) => {
    try {
      const success = await updateUserField(email, { isActive: !isActive });

      if (success) {
        setUsersData((prevUsersData) =>
          sortUsersAlphabetically(
            prevUsersData.map((user) =>
              user.email === email && user.domain === domain
                ? { ...user, isActive: !isActive }
                : user
            )
          )
        );

        // Atualiza subsidiariesUsers também para garantir que combinedUsers reflita a mudança
        setSubsidiariesUsers((prevSubsidiariesUsers) =>
          prevSubsidiariesUsers.map((user) =>
            user.email === email && user.domain === domain
              ? { ...user, isActive: !isActive }
              : user
          )
        );
      }
    } catch (error) {
      console.warn("[toggleUserStatus] Erro: ", error);
    }
  };

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  // Função de upload, quando o arquivo for selecionado
  const handleFileUpload = async (event) => {
    const file = event.target.files[0]; // Captura o arquivo selecionado
    const reader = new FileReader();

    reader.onload = async (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: "binary" }); // Lê o arquivo
      const sheetName = workbook.SheetNames[0]; // Pega o nome da primeira planilha
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet); // Converte a planilha para JSON
      setLoading(true); // Inicia o carregamento
      try {
        await processUsersFromFile(jsonData); // Processa os dados
      } catch (error) {
        console.warn("[handleFileUpload] Erro processando planilha: ", error);
      } finally {
        setLoading(false); // Finaliza o carregamento
      }
    };

    reader.readAsBinaryString(file); // Lê o arquivo como binário
  };

  const processUsersFromFile = async (jsonData) => {
    const batchSize = 500;
    const failedEmails = []; // Contas que não puderam ser criadas no Auth

    const canAddUser = (userRole, targetRole) => {
      if (
        userRole === "admin_global" &&
        ["admin_global", "admin_local", "user"].includes(targetRole)
      )
        return true;
      if (
        userRole === "admin_local" &&
        ["admin_local", "user"].includes(targetRole)
      )
        return true;
      if (userRole === "admin_local" && targetRole === "admin_global") {
        return false;
      }
      return false;
    };

    const usersByDomain = jsonData.reduce((acc, row) => {
      const email = row.email ? row.email.trim() : null;
      if (!email) return acc;
      const domain = extractDomainFromEmail(email);
      if (!acc[domain]) acc[domain] = [];
      acc[domain].push(row);
      return acc;
    }, {});

    for (const domain in usersByDomain) {
      const userDomainRef = doc(db, "regional_enterprises", domain);
      const userDomainDoc = await getDoc(userDomainRef);

      if (!userDomainDoc.exists()) {
        const otherDomains = doc(db, "regional_enterprises", "other_users");
        const otherDomainsDoc = await getDoc(otherDomains);

        if (!otherDomainsDoc.exists()) {
          continue;
        }

        let usersMap = otherDomainsDoc.data().users || {};
        const usersForDomain = usersByDomain[domain];

        for (let i = 0; i < usersForDomain.length; i += batchSize) {
          const batch = writeBatch(db);
          const currentBatch = usersForDomain.slice(i, i + batchSize);

          for (const row of currentBatch) {
            const email = row.email.trim();
            const name = row.name ? row.name.trim() : "N/A";
            const userIsActive =
              row.status?.toLowerCase?.() === "active" ||
              String(row.isActive).toLowerCase() === "true" ||
              row.isActive === true ||
              row.isActive === 1;
            const role = row.role;
            const subsidiaries = Array.isArray(row.subsidiaries)
              ? row.subsidiaries
              : row.subsidiaries
              ? row.subsidiaries.split(";").map((s) => s.trim())
              : [];
            const isDefaultPassword = true;

            if (!canAddUser(userRole, role)) {
              continue;
            }

            // Get all modules for user's subsidiaries (includes both visible and subsidiary-specific)
            const userModules = await getModulesForUserSubsidiaries(
              subsidiaries
            );

            if (!usersMap[email]) {
              // Sem conta no Auth o usuário não consegue logar: não grava no Firestore
              const registered = await registerUser(email, "P@ssw0rd123");
              if (!registered) {
                failedEmails.push(email);
                continue;
              }

              usersMap[email] = {
                name,
                isActive: userIsActive,
                role,
                accessibleModules: [...userModules], // Include all applicable modules
                email,
                subsidiaries,
                isDefaultPassword,
              };
            } else {
              // Usuário existe - atualiza TODOS os campos da planilha
              usersMap[email] = {
                ...usersMap[email], // Mantém campos não vindos da planilha (ex: isDefaultPassword)
                name,
                isActive: userIsActive,
                role,
                accessibleModules: [...userModules],
                email,
                subsidiaries,
              };
            }
          }

          batch.set(otherDomains, { users: usersMap }, { merge: true });
          await batch.commit();
        }
        continue;
      }

      let usersMap = userDomainDoc.data().users || {};
      const usersForDomain = usersByDomain[domain];

      for (let i = 0; i < usersForDomain.length; i += batchSize) {
        const batch = writeBatch(db);
        const currentBatch = usersForDomain.slice(i, i + batchSize);

        for (const row of currentBatch) {
          const email = row.email.trim();
          const name = row.name ? row.name.trim() : "N/A";
          const userIsActive =
            row.status?.toLowerCase?.() === "active" ||
            String(row.isActive).toLowerCase() === "true" ||
            row.isActive === true ||
            row.isActive === 1;
          const role = row.role;
          const subsidiaries = Array.isArray(row.subsidiaries)
            ? row.subsidiaries
            : row.subsidiaries
            ? row.subsidiaries.split(";").map((s) => s.trim())
            : [];

          if (!canAddUser(userRole, role)) {
            continue;
          }

          // GET todos os modulos do usuario, incluindo os visiveis a todos
          const userModules = await getModulesForUserSubsidiaries(subsidiaries);

          if (!usersMap[email]) {
            await registerUser(email, "P@ssw0rd123");

            usersMap[email] = {
              name,
              isActive: userIsActive,
              role,
              accessibleModules: [...userModules], // Inclui modulos visiveis para todos
              email,
              subsidiaries,
            };
          } else {
            // Usuário existe - atualiza TODOS os campos da planilha
            usersMap[email] = {
              ...usersMap[email], // Mantém campos não vindos da planilha
              name,
              isActive: userIsActive,
              role,
              accessibleModules: [...userModules],
              email,
              subsidiaries,
            };
          }
        }

        batch.set(userDomainRef, { users: usersMap }, { merge: true });
        await batch.commit();
      }
    }

    // Module access is now handled directly in user creation above
    // No need to call processSubsidiariesForModules anymore

    loadUsersData(); // Recarrega os dados de usuários após o processamento

    if (failedEmails.length > 0) {
      Swal.fire({
        title: "Some users were not created",
        text: `Could not create the login account for: ${failedEmails.join(", ")}. Check the browser console for details.`,
        icon: "warning",
        confirmButtonText: "OK",
        customClass: {
          popup: "custom-popup",
        },
      });
    }
  };

  // Cria a conta na instância separada (userCreationAuth) para não trocar a
  // sessão do admin. Retorna true se a conta existe ao final (criada ou já existente).
  const registerUser = async (email, password) => {
    try {
      await createUserWithEmailAndPassword(userCreationAuth, email, password);
      await signOut(userCreationAuth);
      return true;
    } catch (error) {
      if (error.code === "auth/email-already-in-use") {
        return true;
      }
      console.warn(`[registerUser] Erro criando conta ${email}: `, error);
      return false;
    }
  };

  useEffect(() => {
    loadUsersData();
  }, []);

  // Função para abrir o popup de edição
  const openEditPopup = (user) => {
    setEditingUser(user);
    setEditedName(user.name); // Inicializa com o nome atual
  };

  // Função para fechar o popup
  const closeEditPopup = () => {
    setEditingUser(null);
  };

  const saveEditedUser = async () => {
    try {
      const userDomainRef = doc(db, "regional_enterprises", editingUser.domain);
      const userDomainDoc = await getDoc(userDomainRef);

      if (userDomainDoc.exists()) {
        const usersMap = userDomainDoc.data().users;

        if (usersMap[editingUser.email]) {
          const userModules = await getModulesForUserSubsidiaries(
            editingUser.subsidiaries
          );

          usersMap[editingUser.email] = {
            ...usersMap[editingUser.email],
            name: editedName,
            role: editingUser.role,
            isActive: editingUser.isActive,
            subsidiaries: editingUser.subsidiaries, // Salva as subsidiárias
            accessibleModules: userModules, // Update module access based on new subsidiaries
          };

          await updateDoc(userDomainRef, { users: usersMap });

          // Recarrega dados para garantir consistência após mudança de subsidiárias
          await loadUsersData();

          // Recarrega subsidiariesUsers
          const updatedSubsidiariesUsers = await getUserBySubsidiaries(
            userInformations.subsidiaries
          );
          setSubsidiariesUsers(updatedSubsidiariesUsers);

          closeEditPopup();
        }
      }
    } catch (error) {
      console.error("Erro salvando usuario editado:", error);
    }
  };

  const deleteUser = async (email, domain) => {
    const result = await Swal.fire({
      title: "Delete Confirmation",
      text: `Are you sure you want to delete the user with email: "${email}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "custom-popup", // Personaliza o pop-up
        title: "custom-title", // Personaliza o título
        confirmButton: "custom-confirm-button", // Personaliza o botão de confirmação
        cancelButton: "custom-cancel-button", // Personaliza o botão de cancelamento
      },
    });

    if (!result.isConfirmed) {
      return; // Cancela a exclusão se o usuário não confirmar
    }

    setLoading(true); // Inicia o indicador de carregamento

    try {
      if (domain === "other_users") {
        // Se o domínio for "other_users", remove diretamente do documento
        const otherUsersRef = doc(db, "regional_enterprises", "other_users");
        const otherUsersDoc = await getDoc(otherUsersRef);

        if (otherUsersDoc.exists()) {
          const usersMap = otherUsersDoc.data().users;

          if (usersMap[email]) {
            delete usersMap[email];

            // Atualiza o Firestore
            await updateDoc(otherUsersRef, { users: usersMap });

            // Atualiza a lista de usuários no estado local
            setUsersData((prevUsersData) =>
              prevUsersData.filter((user) => user.email !== email)
            );

            Swal.fire({
              title: "Deleted!",
              text: `User ${email} has been successfully removed.`,
              icon: "success",
              confirmButtonText: "OK",
              customClass: {
                popup: "custom-popup",
                confirmButton: "custom-confirm-button",
              },
            });
          } else {
            Swal.fire({
              title: "Error",
              text: `An error occurred while deleting the user. Please try again.`,
              icon: "error",
              confirmButtonText: "OK",
              customClass: {
                popup: "custom-popup",
                confirmButton: "custom-confirm-button",
              },
            });
          }
        } else {
          Swal.fire({
            title: "Error",
            text: `An error occurred while deleting the user. Please try again.`,
            icon: "error",
            confirmButtonText: "OK",
            customClass: {
              popup: "custom-popup",
              confirmButton: "custom-confirm-button",
            },
          });
        }

        return; // Finaliza a função após remover o usuário de "other_users"
      }

      // Caso contrário, verifica se o domínio do usuário existe no Firestore
      const userDomainRef = doc(db, "regional_enterprises", domain);
      const userDomainDoc = await getDoc(userDomainRef);

      if (userDomainDoc.exists()) {
        Swal.fire({
          title: "Cannot Delete",
          text: `User ${email} cannot be deleted because only users outside the Microsoft domain can be deleted.`,
          icon: "info",
          confirmButtonText: "OK",
          customClass: {
            popup: "custom-popup",
            confirmButton: "custom-confirm-button",
          },
        });
      } else {
        Swal.fire({
          title: "Error",
          text: `User ${email} cannot be deleted because only users outside the Microsoft domain can be deleted.`,
          icon: "error",
          confirmButtonText: "OK",
          customClass: {
            popup: "custom-popup",
            confirmButton: "custom-confirm-button",
          },
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "An error occurred while deleting the user. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          popup: "custom-popup",
          confirmButton: "custom-confirm-button",
        },
      });
    } finally {
      setEditingUser(null); // Fecha o popup de edição
      setLoading(false); // Finaliza o indicador de carregamento
    }
  };

  const loadAvailableSubsidiaries = async () => {
    try {
      const subsidiaryRef = collection(db, "subsidiary");
      const snapshot = await getDocs(subsidiaryRef);

      // Mapeia os documentos para { id, name }
      const subsidiaries = snapshot.docs.map((doc) => ({
        id: doc.id, // ID do documento
        name: doc.data().name, // Campo 'name' do documento
      }));

      setAvailableSubsidiaries(subsidiaries); // Atualiza o estado
    } catch (error) {
      console.warn(
        "[loadAvailableSubsidiaries] Erro carregando subsidiarias disponiveis para o usuario: ",
        error
      );
    }
  };

  // UseEffect para carregar subsidiárias na montagem do componente
  useEffect(() => {
    loadAvailableSubsidiaries();
  }, []);

  return (
    <div className="main-container-user-management">
      <div className="user-management-container">
        {/* Barra de pesquisa com ícones */}
        <div className="search-upload-container">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          {/* Botão de upload */}
          <button className="upload-button" onClick={handleButtonClick}>
            <FaPlus />
          </button>

          {/* Input de arquivo invisível */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx"
            onChange={handleFileUpload}
          />
        </div>

        {/* Tabela de usuários */}
        <div className="table-container">
          {loading ? (
            <div className="loading-screen">
              <div className="spinner"></div>
              <p>Loading Users...</p>
            </div>
          ) : (
            <table className="user-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subsidiary</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Action</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  return (
                    <tr key={`${user.email}-${user.domain}`}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.subsidiaries?.join(", ") || "-"}</td>
                      <td>
                        {user.role === "admin_global" && <span>Global</span>}
                        {user.role === "admin_local" && <span>Local</span>}
                        {user.role === "user" && <span>User</span>}
                      </td>
                      <td>
                        <span
                          style={{
                            display: "inline-block",
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            marginRight: "8px",
                            backgroundColor: user.isActive ? "green" : "red",
                          }}
                        ></span>
                        {user.isActive ? "Active" : "Inactive"}
                      </td>
                      <td>
                        {(userRole === "admin_global" ||
                          (() => {
                            return permittedDomains;
                          })()) && (
                          <button
                            className="toggle-button"
                            onClick={() =>
                              toggleUserStatus(
                                user.email,
                                user.domain,
                                user.isActive
                              )
                            }
                          >
                            <FaSyncAlt />
                          </button>
                        )}
                      </td>
                      <td>
                        <button
                          className="edit-button"
                          onClick={() => openEditPopup(user)}
                        >
                          <FaEdit />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {editingUser && (
            <EditUserModal
              editingUser={editingUser}
              editedName={editedName}
              setEditedName={setEditedName}
              closeEditPopup={closeEditPopup}
              saveEditedUser={saveEditedUser}
              deleteUser={deleteUser}
              userRole={userRole}
              setEditingUser={setEditingUser}
              availableSubsidiaries={availableSubsidiaries}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
